<!--
component: App
author: Ken Nakamura
description: 概要書等閲覧システム アプリケーション・メイン・モジュール
-->
<template>
<div>
  <v-app>
    <AppHeader v-show="!printing" :title="title"
      :backBtnVisible="backBtnVisible"
      :homeBtnVisible="homeBtnVisible"
      :logoutBtnVisible="logoutBtnVisible"
      />
    <MenuNoticeDialog />
    
    <v-main :style="printing ? 'padding:0;' : ''">
      <!-- keep-aliveを使うといくつかの問題は解決するが別の問題が発生するため保留
      <keep-alive>
        <router-view/>
      </keep-alive>
      -->
      <router-view/>
    </v-main>
    <v-dialog v-if="idleDialogAvailable" v-model="idleDialogVisible" width="560" persistent>
      <v-card>
        <v-card-title class="headline">
          操作確認
        </v-card-title>
        <v-card-text class="pt-4">
          5分間操作を確認できませんでした。{{ idleCountdown }}秒後に最初の画面に移動します
          <div class="mt-3">
            マウス操作、キーボード操作、タッチ操作を行うとこの画面は解除されます。
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
    <AppFooter v-show="!printing" :caption="message"/>
  </v-app>
</div>
</template>
<script>
import AppHeader from './components/AppHeader.vue';
import AppFooter from './components/AppFooter.vue';
import MenuNoticeDialog from './components/MenuNoticeDialog.vue';
import { dbglog } from './common/utils.js';

import 'whatwg-fetch';
if (window.fetch === undefined) {
  window.fetch = fetch;
}

const TERMS_AGREED_KEY = 'madogiwa-terms-agreed';
const IDLE_TIMEOUT_MS = 5 * 60 * 1000;
const IDLE_WARNING_SECONDS = 10;
const IDLE_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
const EXCLUDED_IDLE_ROUTES = ['login', 'terms-agreement', 'print', 'print-image', 'print-image-A4', 'print-kosaku', 'kamisearchresultprint'];

export default {
  name: 'App',
  components: {
    AppHeader,
    AppFooter,
    MenuNoticeDialog,
  },
  data: () => ({
    idleDialogVisible: false,
    idleCountdown: IDLE_WARNING_SECONDS,
    idleTimeoutId: null,
    idleCountdownId: null,
  }),
  props: {
  },
  computed: {
    printing () {
      return this.$store.state.printing;
    },
    title () {
      return this.$store.state.caption;
    },
    message () {
      return this.$store.state.message;
    },
    backBtnVisible () {
      return this.$store.state.currentPage > 1;
    },
    homeBtnVisible () {
      return this.$store.state.currentPage > 1;
    },
    logoutBtnVisible () {
      return this.$store.state.currentPage == 1;
    },
    clickRightBtnFlg () {
      return this.$store.state.controlRightButton;
    },
    authority () {
      return Number(this.$store.state.userInfo?.authority || sessionStorage.getItem('madogiwa-authority') || 0);
    },
    currentRouteName () {
      return this.$route?.name || '';
    },
    isMadogiwaTerminal () {
      return this.authority === 5;
    },
    shouldWatchIdle () {
      return this.isMadogiwaTerminal && !this.printing && this.currentRouteName && !EXCLUDED_IDLE_ROUTES.includes(this.currentRouteName);
    },
    idleDialogAvailable () {
      return !!this.currentRouteName && !EXCLUDED_IDLE_ROUTES.includes(this.currentRouteName);
    },
  },
  watch: {
    currentRouteName () {
      this.syncIdleWatch();
    },
    printing () {
      this.syncIdleWatch();
    },
    authority () {
      this.syncIdleWatch();
    },
  },
  created () {
    // API, ロケーション等の設定を読込む
    // publicPath=/ges の配下に配置されるため、アプリの相対パスで取得する
    const url = './connections.json';
    fetch(url)
    .then((response) => {
      dbglog(`connections.json fetch: ${response.status} ${response.url}`);
      if (!response.ok) {
        throw new Error(`connections.json ${response.status}`);
      }
      return response.json();
    })
    .then((data) => {
      dbglog('connections.json loaded');
      this.$store.commit('setOrigins', data);
      this.visited();
    })
    .catch((err) => {
      dbglog(`App.vue loading connection data failed on created: ${err}`);
    });

    // ユーザ設定を読込む
    const urlUserConfig = './userconfig.json';
    fetch(urlUserConfig)
    .then((response) => {
      return response.json();
    })
    .then((data) => {
      this.$store.commit('setUserConfig', data);
    })
    .catch((err) => {
      dbglog(`App.vue loading user config data failed on created: ${err}`);
    });
  },
  mounted () {
    document.title = '横浜市概要書等閲覧システム';
    // 右クリック禁止制御
      let vueCom = this;
      document.oncontextmenu = function() { return vueCom.clickRightBtnFlg; };
    IDLE_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, this.handleActivity, true);
    });

    this.syncIdleWatch();
  },
  beforeDestroy () {
    IDLE_EVENTS.forEach((eventName) => {
      window.removeEventListener(eventName, this.handleActivity, true);
    });
    this.stopIdleWatch();
  },
  methods: {
    /**
     * 訪問者情報取得APIを呼び出し、クライアント種別判定結果をstoreへ反映する。
     * 接続先APIは `apiOrigin + 'api/visitor/1'` を期待し、正常時は
     * `clientIp` や `clientType` を含む訪問者情報オブジェクトを返す前提。
     * 取得した結果は `setClientInfo` で store に保存し、失敗時はログ出力のみで画面遷移は継続する。
     */
    visited () {
      const url = this.$store.state.apiOrigin + 'api/visitor/1';
      dbglog(`App:visited(${url})`);

      fetch(url, {
        method: 'GET',
        mode: 'cors',
      })
      .then((response) => {
        dbglog(response);
        if (response.ok) {
          return response.json();
        }
        else {
          return response.status;
        }
      })
      .then((data) => {
        dbglog(data);
        //this.clientType = data.clientType;
        this.$store.commit('setClientInfo', data); 
        // console.log(`client IP: ${data.clientIp}, type: ${data.clientType},
        // category: ${data.category}, webEnable: ${data.webEnable}`);
      })
      .catch((err) => {
        dbglog(`App.visited(): ${err}`, -1);
      });
    },
    handleActivity () {
      if (!this.shouldWatchIdle) {
        return;
      }

      if (this.idleDialogVisible) {
        this.continueSession();
        return;
      }

      this.resetIdleTimers();
    },
    syncIdleWatch () {
      if (this.shouldWatchIdle) {
        this.startIdleWatch();
      }
      else {
        this.stopIdleWatch();
      }
    },
    startIdleWatch () {
      this.idleDialogVisible = false;
      this.idleCountdown = IDLE_WARNING_SECONDS;
      this.resetIdleTimers();
    },
    stopIdleWatch () {
      this.idleDialogVisible = false;
      this.clearIdleTimeout();
      this.clearIdleCountdown();
      this.idleCountdown = IDLE_WARNING_SECONDS;
    },
    resetIdleTimers () {
      if (!this.shouldWatchIdle) {
        this.stopIdleWatch();
        return;
      }

      this.clearIdleTimeout();
      this.clearIdleCountdown();
      this.idleDialogVisible = false;
      this.idleCountdown = IDLE_WARNING_SECONDS;
      this.idleTimeoutId = window.setTimeout(() => {
        this.openIdleDialog();
      }, IDLE_TIMEOUT_MS);
    },
    openIdleDialog () {
      if (!this.shouldWatchIdle) {
        return;
      }

      this.idleDialogVisible = true;
      this.idleCountdown = IDLE_WARNING_SECONDS;
      this.clearIdleCountdown();
      this.idleCountdownId = window.setInterval(() => {
        this.idleCountdown -= 1;
        if (this.idleCountdown <= 0) {
          this.returnToTerms();
        }
      }, 1000);
    },
    continueSession () {
      this.resetIdleTimers();
    },
    closeOpenModals () {
      this.idleDialogVisible = false;
      this.$store.commit('setMenuNoticeDialogOpen', false);
    },
    returnToTerms () {
      sessionStorage.removeItem(TERMS_AGREED_KEY);
      this.closeOpenModals();
      this.stopIdleWatch();
      if (this.$route.name !== 'terms-agreement') {
        this.$router.replace({ name: 'terms-agreement' });
      }
      else if (this.shouldWatchIdle) {
        this.startIdleWatch();
      }
    },
    clearIdleTimeout () {
      if (this.idleTimeoutId) {
        window.clearTimeout(this.idleTimeoutId);
        this.idleTimeoutId = null;
      }
    },
    clearIdleCountdown () {
      if (this.idleCountdownId) {
        window.clearInterval(this.idleCountdownId);
        this.idleCountdownId = null;
      }
    },
  }
};
</script>

<style lang="scss">
@media screen {
  #v-main {
    margin: 0 !important;
    padding: 0;
  }
}
@media print {
  #v-main {
    padding: 0;
  }
}
</style>
